import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Score Tickets CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://mavs.spinzo.com/?group=corporate-group-offers-f6xp',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Score Tickets',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

/**
 * Resident Mavericks ticket deal: centered "Go Mavs" headline, discount
 * blurb, and an outbound "Score Tickets" CTA. Same layout and copy as the
 * Dallas Mavericks Games page on the Weidner preferred-theme sites.
 */
export function DallasMavericksGames() {
  const ctaLink = useSlotGroup('dallasmavericksgames.cta_link', ctaLinkDef);
  const target = ctaLink._target || '_blank';

  return (
    <section className="pt-10 pb-16 md:pt-14 md:pb-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl md:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="dallasmavericksgames.headline"
          label="Headline"
          defaultValue={
            '<p>Go Mavs, Go <span data-type="field-token" data-field="property_name"></span>!</p>'
          }
          as="h2"
          className="font-heading text-xl sm:text-2xl md:text-3xl font-normal text-[var(--vibe-color-text)] mb-6 [&_p]:m-0"
        />
        <RichTextContent
          path="dallasmavericksgames.blurb"
          label="Page Blurb"
          defaultValue={
            '<p>At <span data-type="field-token" data-field="property_name"></span>, we\'re all about the Mavs! Residents can score discounted Dallas Mavericks NBA home game tickets throughout the season at the best price. Residents can get up to 20% off tickets at the upper level, up to 15% off tickets at the Platinum level, and up to 10% off tickets at the lower level. Residents can buy a maximum of 20 tickets per game. Each game is subject to availability.</p>'
          }
          as="div"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_p:last-child]:mb-0"
        />

        <div className="mt-10">
          <EditableSlotGroup groupPath="dallasmavericksgames.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || 'https://mavs.spinzo.com/?group=corporate-group-offers-f6xp'}
              target={target}
              rel={target === '_blank' ? 'noopener noreferrer' : undefined}
              className="group/mavs relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase overflow-hidden transition-all duration-300 hover:-translate-y-0.5 text-[var(--vibe-color-text-on-primary,white)]"
              style={{
                background:
                  'linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%)',
                boxShadow:
                  '0 2px 6px rgba(0,0,0,0.2), 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent)',
              }}
            >
              <span className="relative z-10">{ctaLink.label || 'Score Tickets'}</span>
              <svg
                className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/mavs:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'DallasMavericksGames',
  displayName: 'Dallas Mavericks Games',
  tokens: {
    'dallasmavericksgames.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue:
        '<p>Go Mavs, Go <span data-type="field-token" data-field="property_name"></span>!</p>',
    },
    'dallasmavericksgames.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we\'re all about the Mavs! Residents can score discounted Dallas Mavericks NBA home game tickets throughout the season at the best price. Residents can get up to 20% off tickets at the upper level, up to 15% off tickets at the Platinum level, and up to 10% off tickets at the lower level. Residents can buy a maximum of 20 tickets per game. Each game is subject to availability.</p>',
    },
  },
  slotGroups: {
    'dallasmavericksgames.cta_link': ctaLinkDef,
  },
};

(DallasMavericksGames as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default DallasMavericksGames;
